import React, { useState } from "react"
import classNames from "classnames"

import { Action, ActionType } from "@tenforce/toolbox-button-push-react"
import { ExtraColorName, ExtraColors } from "@tenforce/toolbox-default-style"
import { FieldItems, TextArea, ReadOnly } from "@tenforce/toolbox-field-react"
import { Symbols } from "@tenforce/toolbox-fontmap"

import { useAppDispatch, useAppSelector } from "../../../../hooks"
import { GlobalState } from "../../../../store"

import { addErrorNotification } from "../../../notifications/actions"

import { IRCAFinding, IRootCauseAnalysisActions } from "../.."
import FindingContextMenu from "./FindingContextMenu"
import Actions from "./Actions"

export interface Props {
    finding: IRCAFinding
    actions: IRootCauseAnalysisActions
}

export const CardFinding = ({ finding, actions }: Props): JSX.Element => {
    const {
        id,
        content,
        isEditable,
        isMarkedAsRootCause,
        nodeType,
        canCreateNewFinding,
    } = finding

    const dispatch = useAppDispatch()
    const [unsyncTitle, setUnsynctitle] = useState(content)

    const {
        labels,
        controls: { isReadOnly },
    } = useAppSelector((state: GlobalState) => state.incidents)

    const classes = classNames("theme-bubble bg-platinum has-hidden-actions", {
        "theme-note": isMarkedAsRootCause,
        [ExtraColors[ExtraColorName.Action].border ?? ""]: isMarkedAsRootCause,
    })

    const placeholder =
        nodeType === "FiveWhysFinding"
            ? labels.placeholderFiveWhys
            : labels.placeholderIshikawa

    return (
        <div
            className={classes}
            data-ft-is-marked-as-root-cause={isMarkedAsRootCause}
            data-ft-component="finding"
            data-ft-id={id}
            data-ft-type={nodeType}
        >
            <FieldItems>
                {!isReadOnly && isEditable ? (
                    <TextArea
                        placeholder={placeholder}
                        autoSize={true}
                        value={unsyncTitle}
                        title={unsyncTitle}
                        data-ft-component="findingInput"
                        data-ft-is-editable={isEditable}
                        onChangedValue={(value): void => setUnsynctitle(value)}
                        onBlur={(): void => {
                            if (unsyncTitle !== content) {
                                if (unsyncTitle === "") {
                                    setUnsynctitle(content)
                                    dispatch(
                                        addErrorNotification(
                                            labels.exceptionContentCannotBeDeleted,
                                        ),
                                    )
                                } else {
                                    actions.updateFindingContent(
                                        finding.id,
                                        unsyncTitle,
                                    )
                                }
                            }
                        }}
                    />
                ) : (
                    <ReadOnly
                        value={unsyncTitle}
                        title={unsyncTitle}
                        data-ft-component="finding-input"
                        data-ft-is-editable={isEditable}
                    />
                )}

                {!isReadOnly && (
                    <>
                        <FindingContextMenu
                            finding={finding}
                            actions={actions}
                        />

                        {canCreateNewFinding && (
                            <Action
                                actionType={ActionType.icon}
                                extraClasses="is-hidden-action"
                                title={labels.addNewFinding}
                                data-ft-component="createFinding"
                                onClick={(): void => {
                                    actions.addNewFinding(finding.id)
                                }}
                            >
                                {Symbols.Create}
                            </Action>
                        )}
                    </>
                )}
            </FieldItems>
            <Actions finding={finding} />
        </div>
    )
}

export default CardFinding
